import { ref, computed } from 'vue';
import store from '@/store';
import { resolveTipoReserva } from '@/utils/reservas/resolveTipoReserva';

// ── HELPERS LOCALES ───────────────────────────────────────────────────────────

/**
 * Calcula las unidades disponibles a partir de los datos de la reserva.
 * Equivale a: total - claimed - cart (mínimo 0).
 *
 * @param {object|null} reservaData - objeto reserva del backend
 * @returns {number|null} null cuando no aplica (reserva no genérica / sin datos)
 */
function calcUnitsAvailable(reservaData) {
    if (!reservaData) return null;
    const total = reservaData.units_total ?? null;
    if (total === null) return null;
    const claimed = reservaData.units_claimed ?? 0;
    const cart = reservaData.units_cart ?? 0;
    return Math.max(0, total - claimed - cart);
}

/**
 * Composable de dominio para el flujo de canjes/reservas del frontend.
 *
 * Encapsula:
 *  - Detección del modo (genérico vs con-recinto, multi-precio vs precio único)
 *  - Guards: puedeConfirmarCantidad, mostrarAvisoComprarCompleta
 *  - Estado de cantidades: cantidadSolicitada (precio único), cantidadesPorPrecio (multi-precio)
 *  - Construcción del payload para guardarDatos
 *  - Ciclo de vida de session_id (guardar datos, borrar sesión)
 *  - Construcción dinámica de los pasos del stepper
 *
 * La vista sólo consume refs/computed y llama a los handlers.
 *
 * @param {object} options
 * @param {Function} options.getVuexAlias       - config.getVuexAlias
 * @param {string}   options.hash               - hash de la reserva
 * @param {string}   options.hashTeatro         - configuracion.hash_teatro
 * @param {object}   options.toast              - this.$toast
 * @param {Function} options.onStepperChange    - callback para avanzar el stepper (this.$refs.stepper.changeStep)
 * @param {Function} options.onStepperReset     - callback para resetear el stepper
 * @param {Function} options.onRouterPush       - callback para navegar (this.$router.push)
 * @param {Function} options.tFn                - función de traducción (this.$t)
 */
export function useReservaFlow({
    getVuexAlias,
    hash,
    hashTeatro,
    toast,
    onStepperChange,
    onStepperReset,
    onRouterPush,
    tFn,
}) {
    // ── ESTADO ────────────────────────────────────────────────────────────────
    const reserva = ref(null);
    const error = ref(false);
    const comprado = ref(false);
    const butacasEnSesion = ref(false);
    const agotado = ref(false);

    // Selección de asientos / zonas
    const asientos = ref([]);
    const entradasPlazas = ref([]);
    const asientos_seleccionados = ref(false);
    const zona_seleccionada = ref(null);
    const seat_map = ref([]);

    // Datos de compra
    const datos_compradores = ref({});
    const formulario = ref(null);
    const jet_id = ref(null);
    const session_id = ref(null);
    const resumen = ref(false);
    const procesar_pago = ref(false);
    const voucher = ref(null);

    // Steps del stepper
    const steps = ref([]);

    // ── ESTADO CANTIDADES ─────────────────────────────────────────────────────

    /** Cantidad en modo precio único */
    const cantidadSolicitada = ref(1);

    /**
     * Cantidades por tipo de precio en modo multi-precio.
     * @type {import('vue').Ref<Array<{ precio_id: number, cantidad_solicitada: number }>>}
     */
    const cantidadesPorPrecio = ref([]);

    // ── COMPUTED: DISPONIBILIDAD LOCAL ───────────────────────────────────────

    /**
     * Unidades totales de la reserva (del campo units_total del backend).
     * Null cuando la reserva no ha cargado o no aplica.
     */
    const unitsTotal = computed(() => reserva.value?.units_total ?? null);

    /**
     * Unidades disponibles derivadas localmente desde reserva.value.
     * Null cuando la reserva no ha cargado o no aplica.
     */
    const unitsAvailable = computed(() => calcUnitsAvailable(reserva.value));

    // ── COMPUTED: MODO DE RESERVA ─────────────────────────────────────────────

    /**
     * Reserva genérica: sin butacas/plazas pre-asignadas y con al menos 1 unidad total.
     */
    const esGenerico = computed(() => {
        if (!reserva.value) return false;
        return (
            unitsTotal.value !== null &&
            unitsTotal.value >= 1 &&
            reserva.value.butacas.length === 0 &&
            reserva.value.plazas.length === 0
        );
    });

    /**
     * Multi-precio: reserva genérica con más de 1 tipo de precio.
     */
    const esMultiPrecio = computed(() => {
        return esGenerico.value &&
            Array.isArray(reserva.value?.precios) &&
            reserva.value.precios.length > 1;
    });

    /**
     * Total de unidades seleccionadas en modo multi-precio.
     */
    const totalCantidadMultiPrecio = computed(() => {
        if (!esMultiPrecio.value) return 0;
        return cantidadesPorPrecio.value.reduce((sum, p) => sum + p.cantidad_solicitada, 0);
    });

    /**
     * Guard: habilita el botón "Confirmar cantidad".
     *
     * Reglas:
     * - comprar_completa + multi-precio: debe seleccionar exactamente unitsTotal.
     * - comprar_completa + precio único: debe seleccionar exactamente unitsTotal.
     * - multi-precio sin comprar_completa: al menos 1 unidad en cualquier precio.
     * - precio único sin comprar_completa: entre 1 y unitsAvailable.
     *
     * Guard de seguridad: si unitsTotal no está definido, no bloqueamos permanentemente.
     */
    const puedeConfirmarCantidad = computed(() => {
        if (esGenerico.value && reserva.value?.comprar_completa) {
            const total = unitsTotal.value;
            if (total == null) return true;
            if (esMultiPrecio.value) {
                return totalCantidadMultiPrecio.value === total;
            }
            return cantidadSolicitada.value === total;
        }
        if (esMultiPrecio.value) {
            return totalCantidadMultiPrecio.value >= 1;
        }
        return cantidadSolicitada.value >= 1 && cantidadSolicitada.value <= unitsAvailable.value;
    });

    /**
     * True cuando la reserva es comprar_completa genérica y el usuario no ha
     * alcanzado el total requerido todavía.
     */
    const mostrarAvisoComprarCompleta = computed(() => {
        if (!esGenerico.value || !reserva.value?.comprar_completa) return false;
        const total = unitsTotal.value;
        if (total == null) return false;
        if (esMultiPrecio.value) {
            return totalCantidadMultiPrecio.value !== total;
        }
        return cantidadSolicitada.value !== total;
    });

    /**
     * Zonas disponibles (con disponibilidad) de la reserva.
     */
    const zonas_disponibles = computed(() => {
        return reserva.value?.zonas.filter(zona => zona.esta_disponible) ?? [];
    });

    // ── COMPUTED: PASOS DEL STEPPER ───────────────────────────────────────────

    // Se calculan después de cargar la reserva en buildSteps()

    // ── CANTIDADES: MULTI-PRECIO ──────────────────────────────────────────────

    /**
     * Devuelve la cantidad seleccionada para un precio.
     * @param {number} precioId
     * @returns {number}
     */
    function getCantidadPorPrecio(precioId) {
        const entrada = cantidadesPorPrecio.value.find(p => p.precio_id === precioId);
        return entrada ? entrada.cantidad_solicitada : 0;
    }

    /**
     * Establece la cantidad para un precio, respetando el límite de disponibles.
     * @param {number} precioId
     * @param {number} valor
     * @param {number} disponibles
     */
    function setCantidadPorPrecio(precioId, valor, disponibles) {
        const cantidad = Math.min(Math.max(0, valor), disponibles);
        const index = cantidadesPorPrecio.value.findIndex(p => p.precio_id === precioId);
        if (index !== -1) {
            cantidadesPorPrecio.value.splice(index, 1, { precio_id: precioId, cantidad_solicitada: cantidad });
        } else {
            cantidadesPorPrecio.value.push({ precio_id: precioId, cantidad_solicitada: cantidad });
        }
    }

    /**
     * Incrementa en 1 la cantidad de un precio.
     * @param {number} precioId
     * @param {number} disponibles
     */
    function incrementarCantidad(precioId, disponibles) {
        const actual = getCantidadPorPrecio(precioId);
        setCantidadPorPrecio(precioId, actual + 1, disponibles);
    }

    /**
     * Decrementa en 1 la cantidad de un precio (mínimo 0).
     * @param {number} precioId
     */
    function decrementarCantidad(precioId) {
        const precio = reserva.value?.precios?.find(p => p.id === precioId);
        const disponibles = precio ? precio.available_units_for_this_price : 0;
        const actual = getCantidadPorPrecio(precioId);
        setCantidadPorPrecio(precioId, actual - 1, disponibles);
    }

    // ── STEPPER ───────────────────────────────────────────────────────────────

    /**
     * Construye los pasos del stepper según el tipo de reserva.
     * Debe llamarse después de que la reserva se haya cargado.
     */
    function buildSteps() {
        const pasos = [];

        if (esGenerico.value) {
            pasos.push({ label: tFn('reservas.seleccionar_cantidad'), slot: 'seleccionCantidad' });
        } else {
            if (!zona_seleccionada.value) {
                pasos.push({ label: tFn('afiliados.seleccionar_zona'), slot: 'seleccionZona' });
                pasos.push({ label: tFn('afiliados.seleccionar_asientos'), slot: 'seleccionAsientos' });
            } else if (!asientos_seleccionados.value) {
                pasos.push({ label: tFn('afiliados.seleccionar_asientos'), slot: 'seleccionAsientos' });
            }
        }

        pasos.push({ label: tFn('afiliados.datos_comprador'), slot: 'datosCompradores' });
        pasos.push({ label: tFn('afiliados.resumen'), slot: 'resumenCompra' });
        pasos.push({ label: tFn('afiliados.pago'), slot: 'pago' });

        steps.value = pasos;
    }

    /**
     * Avanza al siguiente paso del stepper si la cantidad puede confirmarse.
     * @param {Function} changeStep - función del slot del Stepper
     */
    function confirmarCantidad(changeStep) {
        if (puedeConfirmarCantidad.value) {
            changeStep();
        }
    }

    // ── API: CARGA DE RESERVA ─────────────────────────────────────────────────

    /**
     * Carga la información de la reserva desde el backend y configura el estado inicial.
     */
    function getInfoReserva() {
        const params = {
            filters: { tid: hashTeatro },
            hash,
        };

        store.dispatch(getVuexAlias('front.reservas.info'), params)
            .then(res => {
                reserva.value = res.data.data;

                // Reservas sin butacas/plazas que no son genéricas = proceso interrumpido
                if (
                    reserva.value.butacas.length === 0 &&
                    reserva.value.plazas.length === 0 &&
                    unitsTotal.value === null
                ) {
                    butacasEnSesion.value = true;
                }

                // Sin disponibilidad en reserva genérica
                if (esGenerico.value && unitsAvailable.value === 0) {
                    agotado.value = true;
                    return;
                }

                // Reserva comprar_completa: auto-asignar butacas/plazas existentes
                if (reserva.value.comprar_completa) {
                    if (reserva.value.butacas && reserva.value.butacas.length > 0) {
                        reserva.value.butacas.forEach(butaca => {
                            asientos.value.push({
                                asiento: butaca,
                                precios: butaca.precios,
                                descuento: reserva.value.descuento,
                                precio: butaca.precio,
                            });
                        });
                    }
                    if (reserva.value.plazas && reserva.value.plazas.length > 0) {
                        reserva.value.plazas.forEach(butaca => {
                            butaca.descuento = reserva.value.descuento;
                            asientos.value.push(butaca);
                        });
                    }
                    asientos_seleccionados.value = true;
                    zona_seleccionada.value = true;
                } else {
                    if (zonas_disponibles.value.length === 1) {
                        zona_seleccionada.value = zonas_disponibles.value[0];
                        seat_map.value = reserva.value.seat_map.find(
                            zona_map => zona_map.region_id === zona_seleccionada.value.id
                        ).asientos;
                    }
                }

                // Pre-rellenar email si no es el teatro especial (UHmERrsRV19JCGQMg7Is)
                if (hashTeatro !== 'UHmERrsRV19JCGQMg7Is') {
                    datos_compradores.value.email = reserva.value.email;
                }

                // Crear voucher si la reserva tiene descuento
                if (reserva.value.descuento) {
                    voucher.value = {
                        discount_percent: reserva.value.descuento,
                        units: 100,
                        code: 'Venta personalizada',
                    };
                }

                buildSteps();
            })
            .catch(err => {
                if (err.response && err.response.status === 400) {
                    comprado.value = true;
                } else {
                    error.value = true;
                }
            });
    }

    // ── API: GUARDAR DATOS ────────────────────────────────────────────────────

    /**
     * Construye el payload y guarda los datos del comprador.
     * Avanza al stepper al tener éxito; resetea asientos si hay error.
     *
     * @param {Function} resetAsientosFn - función para resetear asientos en caso de error
     * @param {object}   cookiesFn       - objeto con .get() para leer cookies (this.$cookies)
     */
    function guardarDatos(resetAsientosFn, cookiesFn) {
        resumen.value = true;

        // Normalizar precios nulos en los asientos
        asientos.value.forEach(asiento => {
            if (asiento.precio === null) {
                asiento.precio = asiento.precioSeleccionado;
            }
        });
        datos_compradores.value.asientos = asientos.value;

        const params = {
            filters: { tid: hashTeatro },
            hash,
            datos_compradores: datos_compradores.value,
            session_id: session_id.value,
        };

        // Modo genérico: incluir cantidades
        if (esGenerico.value) {
            if (esMultiPrecio.value) {
                params.cantidades_por_precio = cantidadesPorPrecio.value.filter(
                    p => p.cantidad_solicitada > 0
                );
            } else {
                params.cantidad_solicitada = cantidadSolicitada.value;
            }
        }

        // Plazas o butacas
        if (asientos.value.length > 0) {
            if (reserva.value.plazas.length > 0) {
                params.plazas = asientos.value;
            } else {
                params.butacas = asientos.value;
            }
        }

        params.tipo_reserva = resolveTipoReserva(params);

        // Tracking
        if (cookiesFn) {
            const cookie = cookiesFn.get('comunicados-track-id');
            if (cookie) params.token_comunicado = cookie;

            const fbp = cookiesFn.get('_fbp');
            const fbc = cookiesFn.get('_fbc');
            if (fbp && fbc) {
                params.fbp = fbp;
                params.fbc = fbc;
            }
        }

        store.dispatch(getVuexAlias('front.reservas.guardar.datos'), params)
            .then(res => {
                if (res.data.error) {
                    toast.error(res.data.message);
                    if (resetAsientosFn) resetAsientosFn();
                    getInfoReserva();
                } else {
                    jet_id.value = res.data.jet_id;
                    session_id.value = res.data.data.session_id;
                    formulario.value = res.data.formulario;
                    if (onStepperChange) onStepperChange();
                }
            })
            .catch(err => {
                const status = err.response?.status;
                let msg;
                if (!status) {
                    msg = tFn ? tFn('reservas.error_network') : 'Error de conexión. Por favor, inténtalo de nuevo.';
                } else if (status === 422) {
                    msg = err.response.data?.message || (tFn ? tFn('reservas.error_validation') : 'Los datos enviados no son válidos.');
                } else if (status >= 500) {
                    msg = tFn ? tFn('reservas.error_server') : 'El servicio no está disponible. Inténtalo más tarde.';
                } else {
                    msg = 'Los asientos seleccionados no están disponibles o se ha producido un error';
                }
                toast.error(msg);
                if (resetAsientosFn) resetAsientosFn();
                getInfoReserva();
            })
            .finally(() => {
                resumen.value = false;
            });
    }

    // ── API: BORRAR SESIÓN ────────────────────────────────────────────────────

    /**
     * Borra la sesión activa en el backend (usado cuando el usuario vuelve atrás).
     */
    function borrarSesion() {
        const params = {
            filters: {
                session_id: session_id.value,
                tid: hashTeatro,
            },
            session_id: session_id.value,
            tid: hashTeatro,
        };
        store.dispatch(getVuexAlias('front.sesion.delete'), params);
    }

    // ── API: PROCESAR PAGO ────────────────────────────────────────────────────

    /**
     * Procesa el pago: avanza al paso de pago o gestiona el caso de precio 0.
     * @param {boolean} [precio_cero=false]
     */
    function procesarPago(precio_cero = false) {
        if (precio_cero) {
            procesarCompraPrecioCero();
        } else {
            procesar_pago.value = true;
            if (onStepperChange) onStepperChange();
        }
    }

    async function procesarCompraPrecioCero() {
        const params = {
            filters: {},
            carrito_id: session_id.value,
        };

        try {
            const res = await store.dispatch(getVuexAlias('front.events.procesar-compra-precio-cero'), params);
            if (onRouterPush) {
                onRouterPush(`/compra/${session_id.value}/${res.data.n_pedido}`);
            }
        } catch (err) {
            if (toast) {
                toast.error(tFn ? tFn('reservas.error_procesar_compra') : 'Error al procesar la compra. Por favor, inténtalo de nuevo.');
            }
        }
    }

    // ── RESET DE ASIENTOS ─────────────────────────────────────────────────────

    /**
     * Resetea el estado de selección de asientos/zonas.
     * Si la reserva es comprar_completa, sólo retrocede el stepper.
     */
    function resetAsientos() {
        if (reserva.value && reserva.value.comprar_completa) {
            if (onStepperChange) onStepperChange(true);
            return;
        }
        seat_map.value = [];
        zona_seleccionada.value = null;
        asientos.value = [];
        asientos_seleccionados.value = false;
        if (onStepperReset) onStepperReset();
    }

    // ── INICIALIZACIÓN ────────────────────────────────────────────────────────

    getInfoReserva();

    return {
        // Estado principal
        reserva,
        error,
        comprado,
        butacasEnSesion,
        agotado,
        // Selección de asientos
        asientos,
        entradasPlazas,
        asientos_seleccionados,
        zona_seleccionada,
        seat_map,
        // Datos de compra
        datos_compradores,
        formulario,
        jet_id,
        session_id,
        resumen,
        procesar_pago,
        voucher,
        steps,
        // Cantidades
        cantidadSolicitada,
        cantidadesPorPrecio,
        // Computed
        unitsTotal,
        unitsAvailable,
        esGenerico,
        esMultiPrecio,
        totalCantidadMultiPrecio,
        puedeConfirmarCantidad,
        mostrarAvisoComprarCompleta,
        zonas_disponibles,
        // Métodos
        getCantidadPorPrecio,
        setCantidadPorPrecio,
        incrementarCantidad,
        decrementarCantidad,
        confirmarCantidad,
        guardarDatos,
        borrarSesion,
        procesarPago,
        resetAsientos,
        getInfoReserva,
        buildSteps,
    };
}
